import { html, css, LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '../zero-md/zero-md.js';

import { q1TutorialListStyles } from './q1-tutorial-list-styles.js';

import { Q1SpinnerPanel } from '../q1-spinner-panel/q1-spinner-panel.js';

/**
 * Use this to display a list of tutorials required for a particular subset.
 */
export class Q1TutorialList extends LitElement {
  static styles = [ q1TutorialListStyles ];

  static properties = {
    /** Optional. Leave blank to have no thead / title.  */
    title: { type: String, reflect: true },
    
    tutorials: { type: Array },
    tutorial: { type: Object }, // currently selected tutorial
    
    saving: { type: Boolean },
  };

  constructor() {
    super();

    this.tutorials = [];
    this.tutorial = null;

    this.saving = false;
  }

  updated(changedProperties) {
    if (changedProperties.has('tutorials')) {
      this.saving = false; // clear saving after a refresh
    }
  }

  showTutorial(event, tutorialKey) {
    event.preventDefault();

    this.tutorial = this.tutorials.find(t => t.key === tutorialKey);

    if (this.tutorial) {
      this.updateComplete.then(() => {
        this.renderRoot.getElementById('tutorial-dialog').show();

        this.dispatchEvent(new CustomEvent('track-view', {
          detail: { tutorialKey: this.tutorial.key },
          bubbles: true,
          composed: true,
        }));
      });
    }
  }

  closeDialog() {
    this.renderRoot.getElementById('tutorial-dialog').hide();
  }

  markCompleted() {
    this.saving = true; // wait for a refresh to clear this
    this.renderRoot.getElementById('tutorial-dialog').hide();

    this.dispatchEvent(new CustomEvent('mark-completed', {
      detail: { tutorialKey: this.tutorial.key },
      bubbles: true,
      composed: true,
    }));
  }

  handleDialogShow(event) {
    document.body.style.overflow = 'hidden';
    
    event.target.renderRoot?.querySelector('[part="body"]')?.scrollTo({ top: 0 });
  }

  handleDialogHide() {
    document.body.style.overflow = '';
  }

  render() {
    return html`
      <table>
        ${!this.title ? null : html`
          <thead>
            <tr>
              <th colspan="3">${this.title}</th>
            </tr>
          </thead>
        `}
        <tbody>
          ${this.tutorials.map(tutorial => html`
            <tr class=${classMap({
              completed: tutorial.isCompleted,
              updated: tutorial.isUpdatedSinceCompletion,
            })}>
              <td class="icon-col">
                <sl-tooltip content=${tutorial.statusMessage}>
                  <sl-icon library="material" name=${tutorial.statusIcon}></sl-icon>
                </sl-tooltip>
              </td>
              <td class="title-col">
                <a
                  href="#"
                  @click=${(e) => this.showTutorial(e, tutorial.key)}
                >${tutorial.title}</a>
              </td>
              <td class="time-col">
                <div class="td-inner">
                  <sl-tooltip content="Estimated Reading Time">
                    <sl-icon library="material" name="schedule"></sl-icon>
                  </sl-tooltip>
                  <span class="duration">${tutorial.readingTime}</span>
                </div>
              </td>
            </tr>
          `)}
        </tbody>
      </table>

      <q1-spinner-panel label="Saving..." no-bg ?open=${this.saving}></q1-spinner-panel>

      ${this.tutorialDialogTemplate}
    `;
  }

  get tutorialDialogTemplate() {
    return html`
      <sl-dialog
        label=${this.tutorial?.title}
        id="tutorial-dialog"
        @sl-after-show=${this.handleDialogShow}
        @sl-after-hide=${this.handleDialogHide}
      >
        <zero-md>
          <script type="text/markdown">${this.tutorial?.content}</script>

          <template data-append>
            <style>
              .markdown-body {
                background: white;
                color: var(--gray-9);
              }
              
              .markdown-body h1,
              .markdown-body h2 {
                border-bottom: none !important;
              }
            </style>
          </template>
        </zero-md>

        <button-bar slot="footer">
          ${this.tutorial?.isCompleted ? null : html`
            <sl-button
              variant="primary"
              @click="${this.markCompleted}"
            >Mark Completed</sl-button>
          `}

          <sl-button
            variant="default"
            @click="${this.closeDialog}"
          >Close</sl-button>
        </button-bar>
      </sl-dialog>
    `;
  }
}

customElements.define('q1-tutorial-list', Q1TutorialList);
